// DayStartGuide - Morning greeting with daily planning suggestions
// Extracted from UserDashboard.js
import React from 'react';

// Daily quotes for the quote widget
export const DAILY_QUOTES = [
  { text: "Design is not just what it looks like and feels like. Design is how it works.", author: "Steve Jobs" },
  { text: "The details are not the details. They make the design.", author: "Charles Eames" },
  { text: "Good design is obvious. Great design is transparent.", author: "Joe Sparano" },
  { text: "Design creates culture. Culture shapes values. Values determine the future.", author: "Robert L. Peters" },
  { text: "Every great design begins with an even better story.", author: "Lorinda Mamo" },
  { text: "Design is intelligence made visible.", author: "Alina Wheeler" },
  { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
  { text: "Simplicity is the ultimate sophistication.", author: "Leonardo da Vinci" },
  { text: "Have nothing in your houses that you do not know to be useful or believe to be beautiful.", author: "William Morris" },
  { text: "Architecture is the learned game, correct and magnificent, of forms assembled in the light.", author: "Le Corbusier" },
  { text: "Less is more.", author: "Ludwig Mies van der Rohe" },
  { text: "God is in the details.", author: "Ludwig Mies van der Rohe" },
  { text: "Form follows function.", author: "Louis Sullivan" },
  { text: "Make it simple, but significant.", author: "Don Draper" },
  { text: "Design is a plan for arranging elements in such a way as best to accomplish a particular purpose.", author: "Charles Eames" }
];

// Quote Widget - shows daily quote inline
export function QuoteWidgetMini({ darkMode }) {
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  const quoteIndex = dayOfYear % DAILY_QUOTES.length;
  const quote = DAILY_QUOTES[quoteIndex];
  
  return (
    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
      <span className="italic">&ldquo;{quote.text}&rdquo;</span>
      <span className="ml-1 font-medium">— {quote.author}</span>
    </div>
  );
}

// Day Start Guide Component - Morning greeting with daily planning
export function DayStartGuide({ todayTasks, calendarEvents, darkMode, userName = 'there', compact = false }) {
  const hour = new Date().getHours();
  
  // Greeting based on time of day
  const getGreeting = () => {
    if (hour < 6) return { text: 'Working late?', emoji: '🌙', subtext: "Don't forget to rest!" };
    if (hour < 12) return { text: 'Good morning', emoji: '☀️', subtext: "Let's make today count!" };
    if (hour < 17) return { text: 'Good afternoon', emoji: '🌤️', subtext: 'Keep up the momentum!' };
    if (hour < 21) return { text: 'Good evening', emoji: '🌅', subtext: 'Wrapping up the day?' };
    return { text: 'Good night', emoji: '🌙', subtext: 'Rest well!' };
  };
  
  const greeting = getGreeting();
  
  // Calculate day stats
  const overdueCount = todayTasks?.overdue?.length || 0;
  const todayCount = todayTasks?.today?.length || 0;
  const completedCount = todayTasks?.completed?.length || 0;
  const totalTasks = overdueCount + todayCount;
  const eventsToday = calendarEvents?.length || 0;
  
  // Get priority tasks (urgent/high priority or overdue)
  const priorityTasks = [
    ...(todayTasks?.overdue || []),
    ...(todayTasks?.today?.filter(t => t.priority === 'urgent' || t.priority === 'high') || [])
  ].slice(0, 3);
  
  // Get next event
  const now = new Date();
  const upcomingEvents = (calendarEvents || [])
    .filter(e => new Date(e.start) > now)
    .sort((a, b) => new Date(a.start) - new Date(b.start));
  const nextEvent = upcomingEvents[0];
  
  // Day planning suggestions
  const getSuggestions = () => {
    const suggestions = [];
    
    if (overdueCount > 0) {
      suggestions.push({
        icon: '⚠️',
        text: `Clear ${overdueCount} overdue ${overdueCount === 1 ? 'task' : 'tasks'} first`,
        priority: 'high'
      });
    }
    
    if (priorityTasks.length > 0 && overdueCount === 0) {
      suggestions.push({
        icon: '🎯',
        text: `Focus on "${priorityTasks[0].title}"`,
        priority: 'medium'
      });
    }
    
    if (nextEvent) {
      const eventTime = new Date(nextEvent.start);
      const timeStr = eventTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      suggestions.push({
        icon: '📅',
        text: `${nextEvent.title} at ${timeStr}`,
        priority: 'info'
      });
    }
    
    if (totalTasks === 0 && eventsToday === 0) {
      suggestions.push({
        icon: '✨',
        text: 'Clear schedule - great time to plan ahead!',
        priority: 'success'
      });
    }
    
    if (completedCount > 0 && completedCount >= totalTasks && totalTasks > 0) {
      suggestions.push({
        icon: '🎉',
        text: "You're crushing it today!",
        priority: 'success'
      });
    }
    
    return suggestions.slice(0, 3);
  };
  
  const suggestions = getSuggestions();
  
  // Compact mode - for inline banner display
  if (compact) {
    return (
      <div className="flex items-center gap-4">
        <span className="text-3xl hidden sm:block">{greeting.emoji}</span>
        <div>
          <h2 className={`text-xl md:text-2xl font-heading ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
            {greeting.text}, {userName.split(' ')[0]}!
          </h2>
          <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
            {greeting.subtext}
          </p>
        </div>
        {/* Day Summary Stats - inline */}
        <div className={`hidden md:flex items-center gap-4 ml-6 pl-6 border-l ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-200'}`}>
          <div className="text-center">
            <p className={`text-xl font-bold ${overdueCount > 0 ? 'text-red-500' : darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {totalTasks}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Today</p>
          </div>
          <div className="text-center">
            <p className={`text-xl font-bold ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {eventsToday}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Events</p>
          </div>
          <div className="text-center">
            <p className={`text-xl font-bold text-emerald-500`}>
              {completedCount}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Done</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`rounded-xl p-4 mb-4 ${
      darkMode 
        ? 'bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#2A2A2A]' 
        : 'bg-gradient-to-br from-white to-[#FAF9F7] border shadow-sm'
    }`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Greeting */}
        <div className="flex items-center gap-3">
          <span className="text-3xl">{greeting.emoji}</span>
          <div>
            <h2 className={`text-xl md:text-2xl font-heading ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {greeting.text}, {userName.split(' ')[0]}!
            </h2>
            <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
              {greeting.subtext}
            </p>
          </div>
        </div>
        
        {/* Day Summary Stats */}
        <div className="flex items-center gap-4 md:gap-6">
          <div className="text-center">
            <p className={`text-2xl font-bold ${overdueCount > 0 ? 'text-red-500' : darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {totalTasks}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Today</p>
          </div>
          <div className={`h-8 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'}`} />
          <div className="text-center">
            <p className={`text-2xl font-bold ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
              {eventsToday}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Events</p>
          </div>
          <div className={`h-8 w-px ${darkMode ? 'bg-[#2A2A2A]' : 'bg-gray-200'}`} />
          <div className="text-center">
            <p className={`text-2xl font-bold text-emerald-500`}>
              {completedCount}
            </p>
            <p className={`text-xs ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>Done</p>
          </div>
        </div>
      </div>
      
      {/* Start Your Day Suggestions */}
      {suggestions.length > 0 && (
        <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-[#2A2A2A]' : 'border-gray-100'}`}>
          <p className={`text-xs font-medium uppercase tracking-wide mb-2 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
            Start your day
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
                  s.priority === 'high' 
                    ? darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'
                    : s.priority === 'success'
                    ? darkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
                    : darkMode ? 'bg-[#252525] text-[#E5E2DD]' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <span>{s.icon}</span>
                <span>{s.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DayStartGuide;
